// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useCallback, useEffect, useState } from "react";

export interface TableSelection<T> {
  selected: T[];
  setSelected: (rows: T[]) => void;
  clear: () => void;
}

/**
 * Bulk-selection state for a list table, cleared whenever the RESULT SET
 * changes.
 *
 * Without that reset the bulk bar stays armed against rows that are no longer
 * on screen: tick three customers, narrow the Support Level facet, and the bar
 * still reads "Delete 3" — and deletes those three, none of which the viewer
 * can currently see. The hazard predates the filter bar (paging had the same
 * hole) but every facet is a new way in, so the fix belongs here, once, rather
 * than in each of the nine list pages.
 *
 * `queryKey` is whatever identifies the current result set — pass the request
 * params object. It is compared by VALUE (JSON), not by reference, so a params
 * object rebuilt into an equal shape does not silently drop a selection the
 * user is still assembling.
 *
 * Deliberately NOT cleared on a plain refetch (an inline edit, a bulk action's
 * own reload): those re-fetch the SAME query, so the selection still refers to
 * rows the viewer is looking at.
 *
 * CAVEAT — `queryKey` is compared with `JSON.stringify`, which is a value
 * comparison only for PLAIN, JSON-round-trippable data. Every call site today
 * passes the page's plain `listParams` object, for which it is exact. It is
 * NOT safe for:
 *   - a `Map` or `Set` (both stringify to `{}`, so EVERY key looks equal and
 *     the selection is never cleared),
 *   - a class instance (only its own enumerable fields survive),
 *   - `undefined` / function values (dropped from objects entirely),
 *   - an object whose key INSERTION ORDER varies between renders — stringify
 *     preserves insertion order, so `{a,b}` and `{b,a}` compare as different
 *     and the selection is cleared on a query that did not actually change.
 * A future caller needing any of those must pass a stable derived string
 * instead of the raw object.
 */
export function useTableSelection<T>(queryKey: unknown): TableSelection<T> {
  const [selected, setSelected] = useState<T[]>([]);
  const serialized = JSON.stringify(queryKey ?? null);

  useEffect(() => {
    // Functional form so an already-empty selection does not schedule a
    // pointless re-render on every query change (or on mount).
    setSelected((prev) => (prev.length === 0 ? prev : []));
  }, [serialized]);

  const clear = useCallback(() => setSelected([]), []);

  return { selected, setSelected, clear };
}
